import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import OwnerPersonDrawer from "./center/OwnerPersonDrawer";
import OwnerMovementImportDialog from "./center/OwnerMovementImportDialog";
import OwnerRecurringFinanceDialog from "./center/OwnerRecurringFinanceDialog";
import OwnerRecurringRecordDialog from "./center/OwnerRecurringRecordDialog";
import OwnerRecurringPeriodsDialog from "./center/OwnerRecurringPeriodsDialog";
import OwnerRecurringFromTransactionDialog from "./center/OwnerRecurringFromTransactionDialog";
import OwnerProposalQuickDialog from "./center/OwnerProposalQuickDialog";
import OwnerChatGptExportDialog from "./center/OwnerChatGptExportDialog";
import OwnerMoneyRealityStrip from "./center/OwnerMoneyRealityStrip";
import OwnerJarvis2 from "./center/OwnerJarvis2";
import OwnerHourBoard from "./center/OwnerHourBoard";
import OwnerCommitmentTimeDialog from "./center/OwnerCommitmentTimeDialog";
import OwnerWorkLogDialog from "./center/OwnerWorkLogDialog";
import OwnerInboxProcessorDialog from "./center/OwnerInboxProcessorDialog";
import OwnerDayNoteDialog from "./center/OwnerDayNoteDialog";
import OwnerDayFactDialog from "./center/OwnerDayFactDialog";
import {
  OwnerAccountDialog,
  OwnerClientDialog,
  OwnerCollectDialog,
  OwnerCommitmentDialog,
  OwnerFinanceDialog,
  OwnerInitiativeDialog,
  OwnerInteractionDialog,
} from "./center/OwnerQuickDialogs";
import { OwnerRoutineDialog } from "./calendar/OwnerCalendarDialogs";
import { invalidateOwnerJarvisSnapshotCache } from "./jarvis/ownerJarvisSnapshotService";

const ARGENTINA_TZ = "America/Argentina/Mendoza";

function localYmd(value = new Date()) {
  const formatter = new Intl.DateTimeFormat("en-CA", { timeZone: ARGENTINA_TZ, year: "numeric", month: "2-digit", day: "2-digit" });
  const parts = formatter.formatToParts(value).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function OwnerTodayClockHeader() {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const day = new Intl.DateTimeFormat("es-AR", { timeZone: ARGENTINA_TZ, weekday: "long", day: "numeric", month: "long" }).format(now);
  const clock = new Intl.DateTimeFormat("es-AR", { timeZone: ARGENTINA_TZ, hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).format(now);
  return (
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "flex-end" }} spacing={.5} sx={{ px: { xs: .2, sm: .35 }, pt: .15 }}>
      <Box>
        <Typography sx={{ color: "text.secondary", fontWeight: 950, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".09em" }}>Hoy</Typography>
        <Typography sx={{ mt: .1, fontWeight: 1000, fontSize: { xs: 21, sm: 25 }, letterSpacing: "-.035em", textTransform: "capitalize" }}>{day}</Typography>
      </Box>
      <Stack direction="row" spacing={.65} alignItems="center">
        <Typography sx={{ fontWeight: 1000, fontVariantNumeric: "tabular-nums", fontSize: { xs: 27, sm: 32 }, letterSpacing: "-.055em", lineHeight: 1 }}>{clock}</Typography>
        <Chip size="small" label="ARG" variant="outlined" sx={{ height: 21, fontWeight: 1000, fontSize: 9.5 }} />
      </Stack>
    </Stack>
  );
}

const SNAPSHOT_SCOPES = Object.freeze({
  day: ["today", "commitments", "routines", "calendar", "waiting"],
  finance: ["finance"],
  financePeople: ["finance", "people"],
  people: ["people"],
  routine: ["today", "routines", "calendar"],
  commitment: ["today", "commitments", "people", "waiting", "calendar"],
  project: ["today", "projects", "people", "waiting"],
  work: ["today", "commitments", "calendar"],
});

export default function OwnerPanelPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [center, setCenter] = React.useState(null);
  const [assistant, setAssistant] = React.useState(null);
  const [dailyCalendar, setDailyCalendar] = React.useState(null);
  const [dailyEntries, setDailyEntries] = React.useState([]);
  const [assistantCurrency, setAssistantCurrency] = React.useState("ARS");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [dialog, setDialog] = React.useState("");
  const [financeInitialType, setFinanceInitialType] = React.useState("expense");
  const [sourceEntry, setSourceEntry] = React.useState(null);
  const [inboxProcessorOpen, setInboxProcessorOpen] = React.useState(false);
  const [inboxProcessorEntryId, setInboxProcessorEntryId] = React.useState("");
  const [saleToCollect, setSaleToCollect] = React.useState(null);
  const [relationPerson, setRelationPerson] = React.useState(null);
  const [recurringToRecord, setRecurringToRecord] = React.useState(null);
  const [recurringOccurrenceToRecord, setRecurringOccurrenceToRecord] = React.useState(null);
  const [recurringPeriodsPlan, setRecurringPeriodsPlan] = React.useState(null);
  const [recurringToEdit, setRecurringToEdit] = React.useState(null);
  const [transactionToRecurring, setTransactionToRecurring] = React.useState(null);
  const [selectedPerson, setSelectedPerson] = React.useState(null);
  const [personContext, setPersonContext] = React.useState(null);
  const [personLoading, setPersonLoading] = React.useState(false);
  const [personError, setPersonError] = React.useState("");
  const [chatGptPerson, setChatGptPerson] = React.useState(null);
  const [initialScheduledAt, setInitialScheduledAt] = React.useState("");
  const [timeCommitment, setTimeCommitment] = React.useState(null);
  const [workLogAt, setWorkLogAt] = React.useState("");
  const [dayNoteAt, setDayNoteAt] = React.useState("");
  const [dayFact, setDayFact] = React.useState(null);
  const [jarvisFormPreset, setJarvisFormPreset] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      setError("");
      const [response, entriesResponse] = await Promise.all([
        ownerApi.state({ currency: assistantCurrency, date: localYmd() }),
        ownerApi.listLifeEntries({ status: "all", limit: 150 }),
      ]);
      setCenter(response?.state?.center || null);
      setAssistant(response?.state?.assistant || null);
      setDailyCalendar(response?.state?.calendar || null);
      setDailyEntries(entriesResponse?.entries || []);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar el Panel rápido."));
    } finally {
      setLoading(false);
    }
  }, [assistantCurrency]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const refreshFromJarvis = () => load();
    window.addEventListener("owner:jarvis-mutated", refreshFromJarvis);
    return () => window.removeEventListener("owner:jarvis-mutated", refreshFromJarvis);
  }, [load]);

  React.useEffect(() => {
    const action = searchParams.get("jarvisAction");
    if (!action) return;
    const ui = location.state?.ownerJarvisUi || null;
    const preset = ui?.surface === "owner_home" && ui?.form === action && ui?.preset && typeof ui.preset === "object" ? ui.preset : null;
    setJarvisFormPreset(preset);
    if (action === "finance") { setFinanceInitialType(preset?.type || "expense"); setDialog("finance"); }
    if (action === "consumption") { setFinanceInitialType("consumption"); setDialog("finance"); }
    if (["client", "proposal", "commitment", "routine", "recurring", "interaction", "initiative", "import"].includes(action)) setDialog(action);
    if (action === "inboxProcess") { setInboxProcessorEntryId(""); setInboxProcessorOpen(true); }
    if (action === "worklog") setWorkLogAt(new Date().toISOString());
    if (action === "dayNote") setDayNoteAt(new Date().toISOString());
    const next = new URLSearchParams(searchParams);
    next.delete("jarvisAction");
    setSearchParams(next, { replace: true });
  }, [location.state, searchParams, setSearchParams]);

  const loadPersonContext = React.useCallback(async (person, { reset = true } = {}) => {
    const personId = person?.id || person?._id;
    if (!personId) return;
    if (reset) setPersonContext(null);
    setPersonLoading(true);
    setPersonError("");
    try {
      const response = await ownerApi.personContext(personId);
      setPersonContext(response.context);
    } catch (err) {
      setPersonError(formatApiError(err, "No pude cargar la ficha de esta persona."));
    } finally {
      setPersonLoading(false);
    }
  }, []);

  const closeDialogs = () => {
    setDialog("");
    setJarvisFormPreset(null);
    setSourceEntry(null);
    setSaleToCollect(null);
    setRelationPerson(null);
    setRecurringToRecord(null);
    setRecurringOccurrenceToRecord(null);
    setRecurringPeriodsPlan(null);
    setRecurringToEdit(null);
    setTransactionToRecurring(null);
    setInitialScheduledAt("");
  };

  const refreshAfterAction = async (scopes) => {
    invalidateOwnerJarvisSnapshotCache(scopes);
    await load();
    if (selectedPerson) await loadPersonContext(selectedPerson, { reset: false });
  };

  if (loading && !center) return <DashboardLoadingState label="Preparando tu Panel rápido…" minHeight={420} />;

  const currency = assistantCurrency || assistant?.currency || "ARS";

  return (
    <Box sx={{ pb: { xs: 1.5, md: 1 }, maxWidth: 1500, mx: "auto", px: 0 }}>
      <OwnerTodayClockHeader />

      {error ? <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{error}</Alert> : null}

      <Box sx={{ mt: 1 }}>
        <OwnerMoneyRealityStrip
          center={center}
          assistant={assistant}
          currency={currency}
          onCurrencyChange={setAssistantCurrency}
          onOpenFinance={() => navigate("/dashboard/owner/finanzas")}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <OwnerJarvis2
          currency={currency}
          onChanged={(scopes) => refreshAfterAction(scopes || SNAPSHOT_SCOPES.day)}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <OwnerHourBoard
          calendar={dailyCalendar}
          notes={dailyEntries}
          onScheduleAt={(value) => { setInitialScheduledAt(value); setDialog("commitment"); }}
          onLogTime={(commitment) => setTimeCommitment(commitment)}
          onLogAt={(value) => setWorkLogAt(value)}
          onNoteAt={(value) => setDayNoteAt(value)}
          onProcessNote={(entry) => { setInboxProcessorEntryId(String(entry?.id || entry?._id || "")); setInboxProcessorOpen(true); }}
          onReviewFact={(fact) => setDayFact(fact)}
          onRoutineToggle={async (routine) => {
            const id = String(routine?.id || routine?._id || "");
            if (!id) return;
            try {
              await ownerApi.setRoutineCheckin(id, dailyCalendar?.selectedDate || localYmd(), true);
              await refreshAfterAction(SNAPSHOT_SCOPES.routine);
            } catch (err) {
              setError(formatApiError(err, "No pude marcar esta rutina."));
            }
          }}
        />
      </Box>

        <OwnerDayNoteDialog
          open={Boolean(dayNoteAt)}
          occurredAt={dayNoteAt}
          onClose={() => setDayNoteAt("")}
          onSaved={async () => { setDayNoteAt(""); await refreshAfterAction(["today"]); }}
        />

        <OwnerDayFactDialog
          open={Boolean(dayFact)}
          fact={dayFact}
          onClose={() => setDayFact(null)}
          onChanged={() => refreshAfterAction(SNAPSHOT_SCOPES.day)}
        />

        <OwnerInboxProcessorDialog
          open={inboxProcessorOpen}
          onClose={() => { setInboxProcessorOpen(false); setInboxProcessorEntryId(""); }}
          focusEntryId={inboxProcessorEntryId}
          entries={dailyEntries.filter((entry) => entry?.status === "inbox")}
          totalCount={center?.life?.pendingInboxCount || 0}
          initiatives={center?.life?.initiatives || []}
          people={assistant?.clients || center?.people?.focus || []}
          onChanged={(scopes) => refreshAfterAction(scopes || ["today"])}
        />

        <OwnerPersonDrawer
          open={Boolean(selectedPerson)}
          person={selectedPerson}
          context={personContext}
          loading={personLoading}
          error={personError}
          onClose={() => { setSelectedPerson(null); setPersonContext(null); setPersonError(""); }}
          onInteraction={(person) => { setRelationPerson(person); setDialog("interaction"); }}
          onFollowUp={(person) => { setRelationPerson(person); setDialog("commitment"); }}
          onWaiting={(person) => { setRelationPerson(person); setDialog("waiting"); }}
          onWaitingResolved={async (commitment) => {
            try {
              await ownerApi.updateCommitment(commitment.id || commitment._id, { work: { ...(commitment.work || {}), state: "ready", waitingResolution: "Llegó lo esperado." } });
              await refreshAfterAction(SNAPSHOT_SCOPES.commitment);
            } catch (err) {
              setPersonError(formatApiError(err, "No pude resolver esta espera."));
            }
          }}
          onExportChatGpt={(person) => setChatGptPerson(person)}
          onMonthly={(person) => { setRelationPerson(person); setDialog("recurring"); }}
          onProposal={(person) => { setRelationPerson(person); setDialog("proposal"); }}
          onProject={(person) => { setRelationPerson(person); setDialog("initiative"); }}
        />

        <OwnerChatGptExportDialog open={Boolean(chatGptPerson)} person={chatGptPerson} onClose={() => setChatGptPerson(null)} />
        <OwnerProposalQuickDialog open={dialog === "proposal"} onClose={closeDialogs} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.financePeople)} preferredClient={relationPerson} initialValues={dialog === "proposal" ? jarvisFormPreset : null} />
        <OwnerAccountDialog open={dialog === "account"} onClose={closeDialogs} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.finance)} />
        <OwnerFinanceDialog open={dialog === "finance"} onClose={closeDialogs} accounts={center?.finance?.accounts || []} initialType={financeInitialType} initialValues={dialog === "finance" ? jarvisFormPreset : null} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.finance)} />
        <OwnerClientDialog open={dialog === "client"} onClose={closeDialogs} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.people)} initialValues={dialog === "client" ? jarvisFormPreset : null} />
        <OwnerRoutineDialog open={dialog === "routine"} onClose={closeDialogs} selectedDate={dailyCalendar?.selectedDate || localYmd()} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.routine)} initialValues={dialog === "routine" ? jarvisFormPreset : null} />
        <OwnerCommitmentDialog open={["commitment", "followup", "waiting", "inboxCommitment", "inboxReminder"].includes(dialog)} onClose={closeDialogs} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.commitment)} sourceEntry={sourceEntry} initiatives={center?.life?.initiatives || []} person={relationPerson} initialScheduledAt={initialScheduledAt} initialKind={dialog === "followup" || dialog === "waiting" ? "follow_up" : dialog === "inboxReminder" ? "reminder" : ""} initialWorkState={dialog === "waiting" ? "waiting" : ""} initialValues={dialog === "commitment" ? jarvisFormPreset : null} />
        <OwnerCommitmentTimeDialog open={Boolean(timeCommitment)} commitment={timeCommitment} onClose={() => setTimeCommitment(null)} onSaved={async () => { setTimeCommitment(null); await refreshAfterAction(SNAPSHOT_SCOPES.work); }} />
        <OwnerWorkLogDialog open={Boolean(workLogAt)} occurredAt={workLogAt} onClose={() => setWorkLogAt("")} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.work)} />
        <OwnerInteractionDialog open={dialog === "interaction" || dialog === "inboxInteraction"} onClose={closeDialogs} onSaved={() => refreshAfterAction(["today", "people", "commitments"])} person={relationPerson} sourceEntry={dialog === "inboxInteraction" ? sourceEntry : null} people={assistant?.clients || center?.people?.focus || []} />
        <OwnerInitiativeDialog open={dialog === "initiative" || dialog === "inboxInitiative"} onClose={closeDialogs} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.project)} people={assistant?.clients || center?.people?.focus || []} preferredPerson={relationPerson} sourceEntry={dialog === "inboxInitiative" ? sourceEntry : null} initialValues={dialog === "initiative" ? jarvisFormPreset : null} />
        <OwnerCollectDialog open={dialog === "collect"} onClose={closeDialogs} sale={saleToCollect} accounts={center?.finance?.accounts || []} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.financePeople)} />
        <OwnerMovementImportDialog open={dialog === "import"} onClose={closeDialogs} onCommitted={() => refreshAfterAction(SNAPSHOT_SCOPES.finance)} />
        <OwnerRecurringFinanceDialog open={dialog === "recurring"} onClose={closeDialogs} accounts={center?.finance?.accounts || []} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.financePeople)} preferredClient={relationPerson} plan={recurringToEdit} initialValues={dialog === "recurring" ? jarvisFormPreset : null} />
        <OwnerRecurringRecordDialog open={dialog === "recurringRecord"} onClose={closeDialogs} plan={recurringToRecord} occurrence={recurringOccurrenceToRecord} accounts={center?.finance?.accounts || []} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.financePeople)} />
        <OwnerRecurringPeriodsDialog open={dialog === "recurringPeriods"} plan={recurringPeriodsPlan} onClose={closeDialogs} onRecord={(occurrence) => { setRecurringToRecord(recurringPeriodsPlan); setRecurringOccurrenceToRecord(occurrence); setDialog("recurringRecord"); }} />
        <OwnerRecurringFromTransactionDialog open={dialog === "recurringFromTransaction"} onClose={closeDialogs} transaction={transactionToRecurring} recurring={center?.finance?.recurring} onSaved={() => refreshAfterAction(SNAPSHOT_SCOPES.financePeople)} />
    </Box>
  );
}
